import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ChevronRight, Pencil, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";
import { languages } from "@/utils/i18n";
import { formatDateTimeCompact } from "@/utils/relative-time";

import {
  type AdministrativeArea,
  type AdministrativeAreaType,
  type SetAdministrativeAreasModalOptions,
} from "../../types";

interface ListProps {
  administrativeAreas?: AdministrativeArea[];
  administrativeAreaTypes?: AdministrativeAreaType[];
  errorMessage?: string;
  isFetching?: boolean;
  onSortingChange: OnChangeFn<SortingState>;
  searchTerm?: string;
  setModalOptions: SetAdministrativeAreasModalOptions;
  sorting: SortingState;
}

interface AdministrativeAreaColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type AdministrativeAreaColumnDef = ColumnDef<AdministrativeArea, unknown> & {
  meta?: AdministrativeAreaColumnMeta;
};

type NamedEntity = AdministrativeArea | AdministrativeAreaType;

const PAGE_SIZE = 20;
const EMPTY_VALUE = "-";
const SYSTEM_USER_LABEL = "System";
const COMPACT_HEADER_CLASS = "px-3 py-2 !whitespace-normal leading-tight";
const ACTION_BUTTON_CLASS = "w-[4.75rem]";

export const DEFAULT_SORTING: SortingState = [{ id: "name", desc: false }];

export const ADMINISTRATIVE_AREA_SORT_OPTIONS = [
  { id: "name", translationKey: "configuration.common.name" },
  { id: "translation", translationKey: "administrativeAreas.list.columns.translation" },
  { id: "type", translationKey: "administrativeAreas.list.columns.type" },
  { id: "parent", translationKey: "administrativeAreaTypes.parent" },
  { id: "wfpId", translationKey: "administrativeAreas.wfpId" },
  { id: "lastUpdated", translationKey: "configuration.list.column.lastUpdated" },
  { id: "created", translationKey: "configuration.list.column.created" },
  { id: "active", translationKey: "configuration.common.active" },
] as const;

const getColumnMeta = (columnDef: ColumnDef<AdministrativeArea, unknown>) =>
  columnDef.meta as AdministrativeAreaColumnMeta | undefined;

const getName = (entity?: NamedEntity | null) => entity?.name?.trim() || "";

const createLookup = <TEntity extends NamedEntity>(entities: TEntity[]) =>
  new Map(
    entities.flatMap(entity =>
      entity.id === undefined || entity.id === null ? [] : [[String(entity.id), entity] as const]
    )
  );

const getRelatedName = (id: AdministrativeArea["id"], lookup: Map<string, NamedEntity>) =>
  id === undefined || id === null ? "" : getName(lookup.get(String(id)));

const getTranslatedName = (area: AdministrativeArea, languageCode: string) =>
  area.name_translations?.[languageCode]?.trim() || getName(area);

const getTimestamp = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const getFullName = (user?: AdministrativeArea["creation_user"]) =>
  [user?.first_name, user?.last_name]
    .map(name => name?.trim())
    .filter(Boolean)
    .join(" ") || SYSTEM_USER_LABEL;

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

export const filterAdministrativeAreas = (
  administrativeAreas: AdministrativeArea[],
  typeLookup: Map<string, AdministrativeAreaType>,
  searchTerm?: string
) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  if (!query) {
    return administrativeAreas;
  }

  return administrativeAreas.filter(area =>
    [getName(area), getRelatedName(area.type, typeLookup)].some(value =>
      normalizeSearchValue(value).includes(query)
    )
  );
};

const PlaceholderValue = () => <span className="text-neutral-alpha-300">{EMPTY_VALUE}</span>;

const AuditCell = ({
  dateTime,
  t,
  user,
}: {
  dateTime?: string | null;
  t: TFunction;
  user?: AdministrativeArea["creation_user"];
}) => {
  const formattedDateTime = formatDateTimeCompact(dateTime);

  if (!formattedDateTime) {
    return <PlaceholderValue />;
  }

  return (
    <div className="min-w-0 leading-tight">
      <div className="text-foreground">{formattedDateTime}</div>
      <div className="mt-1 truncate text-muted-foreground text-xs">
        {t("configuration.byFullName", { fullName: getFullName(user) })}
      </div>
    </div>
  );
};

const createColumns = ({
  languageCode,
  languageLabel,
  onOpenModal,
  parentLookup,
  t,
  typeLookup,
}: {
  languageCode: string;
  languageLabel: string;
  onOpenModal: (area: AdministrativeArea, type: "edit" | "view") => void;
  parentLookup: Map<string, AdministrativeArea>;
  t: TFunction;
  typeLookup: Map<string, AdministrativeAreaType>;
}): AdministrativeAreaColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.view")} / {t("common.edit")}
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-start gap-2">
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          className={ACTION_BUTTON_CLASS}
          onClick={() => onOpenModal(row.original, "view")}
        >
          {t("common.view")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </WFPButton>
        <WFPButton
          type="button"
          variant="secondary"
          size="sm"
          className={ACTION_BUTTON_CLASS}
          onClick={() => onOpenModal(row.original, "edit")}
        >
          <Pencil className="size-4" aria-hidden="true" />
          {t("common.edit")}
        </WFPButton>
      </div>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "w-0 px-3 align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "name",
    accessorFn: getName,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.name")} />
    ),
    cell: ({ row }) => getName(row.original) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName:
        "min-w-40 max-w-64 whitespace-normal px-3 align-top font-medium text-foreground",
      headerClassName: cn("min-w-40 max-w-64", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "translation",
    accessorFn: area => getTranslatedName(area, languageCode),
    header: ({ column }) => (
      <SortableColumnHeader
        column={column}
        label={t("commodities.list.column.translation", { language: languageLabel })}
      />
    ),
    cell: ({ row }) => (
      <span className="block whitespace-normal leading-tight text-foreground">
        {getTranslatedName(row.original, languageCode)}
      </span>
    ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 max-w-64 px-3 align-top",
      headerClassName: cn("min-w-40 max-w-64", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "type",
    accessorFn: area => getRelatedName(area.type, typeLookup),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("administrativeAreas.list.columns.type")} />
    ),
    cell: ({ row }) => getRelatedName(row.original.type, typeLookup) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-48 whitespace-normal px-3 align-top",
      headerClassName: cn("min-w-32 max-w-48", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "parent",
    accessorFn: area => getRelatedName(area.parent, parentLookup),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("administrativeAreaTypes.parent")} />
    ),
    cell: ({ row }) => getRelatedName(row.original.parent, parentLookup) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-48 whitespace-normal px-3 align-top",
      headerClassName: cn("min-w-32 max-w-48", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "wfpId",
    accessorFn: area => area.wfpid?.trim() || "",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("administrativeAreas.wfpId")} />
    ),
    cell: ({ row }) => row.original.wfpid?.trim() || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-24 max-w-40 px-3 align-top",
      headerClassName: cn("min-w-24 max-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "lastUpdated",
    accessorFn: area => getTimestamp(area.last_update_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "created",
    accessorFn: area => getTimestamp(area.creation_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "active",
    accessorFn: area => Boolean(area.active),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.common.active")} />
    ),
    cell: ({ row }) => (
      <Badge variant={row.original.active ? "success" : "destructive"}>
        {t(row.original.active ? "common.yes" : "common.no")}
      </Badge>
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-24 px-3 align-top",
      headerClassName: cn("min-w-24", COMPACT_HEADER_CLASS),
    },
  },
];

export const List = ({
  administrativeAreas = [],
  administrativeAreaTypes = [],
  errorMessage = "",
  isFetching = false,
  onSortingChange,
  searchTerm = "",
  setModalOptions,
  sorting,
}: ListProps) => {
  const { i18n, t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const parentLookup = useMemo(() => createLookup(administrativeAreas), [administrativeAreas]);
  const typeLookup = useMemo(
    () => createLookup(administrativeAreaTypes),
    [administrativeAreaTypes]
  );
  const languageLabel = useMemo(
    () =>
      (languages as { label: string; value: string }[]).find(
        language => language.value === i18n.language
      )?.label ?? i18n.language,
    [i18n.language]
  );
  const columns = useMemo(
    () =>
      createColumns({
        languageCode: i18n.language,
        languageLabel,
        onOpenModal: (area, type) =>
          setModalOptions({ id: area.id ?? undefined, isOpen: true, type }),
        parentLookup,
        t,
        typeLookup,
      }),
    [i18n.language, languageLabel, parentLookup, setModalOptions, t, typeLookup]
  );
  const filteredAdministrativeAreas = useMemo(
    () => filterAdministrativeAreas(administrativeAreas, typeLookup, searchTerm),
    [administrativeAreas, searchTerm, typeLookup]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [searchTerm, sorting]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredAdministrativeAreas.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredAdministrativeAreas.length]);

  const table = useReactTable({
    columns,
    data: filteredAdministrativeAreas,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (area, index) =>
      area.id === undefined || area.id === null ? `${index}-${getName(area)}` : String(area.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: { pagination, sorting },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={getColumnMeta(header.column.columnDef)?.headerClassName}
                  aria-sort={getColumnSortAria(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("administrativeAreas.list.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {rows.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
